// Flash cards for the lesson system: note reading, chord recognition, and
// scale recognition, each answered by PLAYING it on the MIDI keyboard. Pure
// data + matchers; the My Practice view renders prompts and feeds noteOns.

{floor, max, min} := Math

{ noteName } from ../util.civet

export type CardKind = "note" | "chord" | "scale"

export type Card =
  id: string
  kind: CardKind
  // note: the exact pitch to play (staff-position reading)
  n?: number
  // chord: pitch classes relative to C (any octave, any voicing/order)
  pcs?: number[]
  // scale: tonic pitch class; played ascending one octave from any octave
  tonicPc?: number
  // prompt line under the staff / instead of it
  label: string
  // revealed after a miss
  answer: string

// What the pool is biased toward: the learner's chosen piece
export type PieceBias =
  title?: string
  // key signature as sharps(+)/flats(-) count, the song event's `key`
  key: number
  // how often each exact midi pitch sounds in the piece
  noteCounts: Record<number, number>

mod := (n: number, m: number) -> ((n % m) + m) % m

// pitch-class name without the octave ("C", "F#")
export pcName := (pc: number) ->
  noteName(60 + mod(pc, 12)).replace /-?\d+$/, ""

// Major-key tonic pitch class from a sharps/flats count (circle of fifths)
export tonicOfKey := (key: number) ->
  mod key * 7, 12

MAJOR_STEPS := [0, 2, 4, 5, 7, 9, 11, 12]

export noteCard := (n: number): Card ->
  id: `note:${n}`
  kind: "note"
  n: n
  label: "Play this note"
  answer: noteName n

export chordCard := (rootPc: number, quality: "maj" | "min"): Card ->
  root := mod rootPc, 12
  offsets := if quality is "maj" then [0, 4, 7] else [0, 3, 7]
  pcs := offsets.map (o) -> mod root + o, 12
  id: `chord:${quality}:${root}`
  kind: "chord"
  pcs: pcs
  label: `Play the ${pcName root}${if quality is "min" then "m" else ""} chord`
  answer: pcs.map(pcName).join "–"

export scaleCard := (tonicPc: number): Card ->
  tonic := mod tonicPc, 12
  id: `scale:major:${tonic}`
  kind: "scale"
  tonicPc: tonic
  label: `Play the ${pcName tonic} major scale, ascending`
  answer: MAJOR_STEPS.map((s) -> pcName tonic + s).join " "

WHITE_PCS := [0, 2, 4, 5, 7, 9, 11]
BLACK_PCS := [1, 3, 6, 8, 10]

// Introduction order without a piece: middle-octave whites out, then blacks
defaultNoteOrder := ->
  order: number[] := []
  for octaveBase of [60, 48, 72]
    for pc of WHITE_PCS
      order.push octaveBase + pc
  for octaveBase of [60, 48, 72]
    for pc of BLACK_PCS
      order.push octaveBase + pc
  order

// Diatonic triads worth drilling first: I, IV, V, vi (then ii, iii)
diatonicChords := (key: number): [number, "maj" | "min"][] ->
  t := tonicOfKey key
  [
    [t, "maj"], [t + 5, "maj"], [t + 7, "maj"], [t + 9, "min"]
    [t + 2, "min"], [t + 4, "min"]
  ]

// The full ordered pool: introduction order, most valuable first. With a
// piece bias, the piece's own notes (by frequency), key, and diatonic
// chords lead; the defaults follow so the pool never runs dry.
export buildPool := (bias?: PieceBias): Card[] ->
  key := bias?.key ?? 0

  noteOrder: number[] := []
  seen := new Set<number>
  push := (n: number) ->
    return unless 36 <= n <= 84
    return if seen.has n
    seen.add n
    noteOrder.push n

  if bias
    Object.keys bias.noteCounts
      .map (k) -> parseInt k, 10
      .sort (a, b) -> bias.noteCounts[b] - bias.noteCounts[a] or a - b
      .forEach push
  defaultNoteOrder().forEach push

  notes := noteOrder.map noteCard

  chords: Card[] := []
  chordSeen := new Set<string>
  for [root, quality] of [...diatonicChords(key), ...diatonicChords(0)]
    card := chordCard root, quality
    continue if chordSeen.has card.id
    chordSeen.add card.id
    chords.push card

  scales: Card[] := []
  scaleSeen := new Set<string>
  for tonic of [tonicOfKey(key), 0, 7, 5]
    card := scaleCard tonic
    continue if scaleSeen.has card.id
    scaleSeen.add card.id
    scales.push card

  // Weave the kinds so a session rotates: mostly notes, a chord every few
  // cards, a scale now and then
  pool: Card[] := []
  ni .= 0
  ci .= 0
  si .= 0
  while ni < notes.length or ci < chords.length or si < scales.length
    before := pool.length
    for _ of [0...3]
      pool.push notes[ni++] if ni < notes.length
    pool.push chords[ci++] if ci < chords.length
    if si < scales.length and (pool.length % 8 < 4 or pool.length is before)
      pool.push scales[si++]
  pool

// Reads the learner's piece off a song: key signature at the top plus a
// histogram of every sounded pitch. `song` is structurally typed so tests
// can pass a plain object.
export pieceBiasFromSong := (song: {
  events: {type: string, n?: number}[]
  keySignatureEventAtBeat: (beat: number) => {key: number}
  title?: () => string?
}): PieceBias ->
  noteCounts: Record<number, number> := {}
  song.events.forEach (e) ->
    if e.type is "noteOn" and e.n?
      noteCounts[e.n] = (noteCounts[e.n] ?? 0) + 1

  title: song.title?() ?? undefined
  key: song.keySignatureEventAtBeat(0).key
  noteCounts: noteCounts

// ---- Matchers: play-to-advance state machines --------------------------
//
// input(pitch) -> "correct" | "wrong" | "progress"; reset() starts the
// attempt over (after a miss the learner replays the card cleanly).

export type MatchResult = "correct" | "wrong" | "progress"

export type Matcher =
  input: (n: number) => MatchResult
  reset: () => void

export matcherFor := (card: Card): Matcher ->
  switch card.kind
    "note"
      input: (n: number) ->
        if n is card.n! then "correct" else "wrong"
      reset: ->

    "chord"
      // collect the chord's pitch classes in any order/octave (rolled ok);
      // any pitch outside the chord fails the attempt
      collected .= new Set<number>
      {
        input: (n: number) ->
          pc := mod n, 12
          return "wrong" unless card.pcs!.includes pc
          collected.add pc
          if collected.size is card.pcs!.length then "correct" else "progress"
        reset: ->
          collected = new Set
      }

    "scale"
      // ascending major scale from any octave: the first tonic anchors it,
      // then each next scale degree must follow exactly
      i .= 0
      base .= 0
      {
        input: (n: number) ->
          if i is 0
            return "wrong" unless mod(n, 12) is card.tonicPc!
            base = n
            i = 1
            return "progress"
          return "wrong" unless n is base + MAJOR_STEPS[i]
          i++
          if i is MAJOR_STEPS.length then "correct" else "progress"
        reset: ->
          i = 0
      }
